<script lang="ts" setup>
import {
  Groups,
  LoadingSpaceDimensions,
  TechDataVehicle,
  VehicleDimensions,
  Weights,
  Body,
  Engine,
  EmissionsConsumption,
  Translations,
  TransmissionMode,
} from '@/types/types';
import { tableTitles } from '../utils/tableLabels';
import TableValuesItem from '@/components/table-values-item.vue';
import { getDefaultLocaleValue } from '../locales/default';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import TechDataModalHeader from './tech-data-modal-header.vue';
import { useTracking } from '../composables/useTracking';
import { vHasScroll } from '../directives/v-has-scroll';
import {
  normalizeTransmission,
  hasMultipleTransmissions,
} from '@/utils/normalize';

const props = defineProps<{
  translations: Translations;
  vehicle: TechDataVehicle;
  vehicles: TechDataVehicle[];
  isOpen: boolean;
  locale: string;
  modelSeries: string;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  transmissionMode: TransmissionMode | null;
}>();
const mode = computed<TransmissionMode | null>(() => {
  return normalizeTransmission(props.transmissionMode);
});

const showTransmissionFilter = computed(() =>
  hasMultipleTransmissions(props.vehicles),
);

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const animatingIn = ref(false);
let lastOpenedByEvent: EventTarget | null = null;
let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

// Tracking
const { trackModalScroll } = useTracking(props.modelSeries);

const tableWrapperRef = ref<HTMLElement | null>(null);
let hasTrackedScrollEnd = false;

watch(
  () => props.isOpen,
  async (open: boolean) => {
    const dialog = dialogRef.value;
    if (!dialog) return;

    if (open) {
      animatingIn.value = false;
      lastOpenedByEvent = document.activeElement;
      document.body.style.overflow = 'hidden';

      dialog.showModal();

      await nextTick();

      // Reset scroll position when opening the modal.
      // Without this reset the modal would reopen at the previous scroll position.
      dialog.scrollTop = 0;

      requestAnimationFrame(() => {
        animatingIn.value = true;

        dialog.removeEventListener('scroll', throttledScrollHandler);
        dialog.addEventListener('scroll', throttledScrollHandler, {
          passive: true,
        });
      });

      hasTrackedScrollEnd = false;
    } else {
      animatingIn.value = false;
      document.body.style.overflow = '';

      dialog.removeEventListener('scroll', throttledScrollHandler);

      await waitForDialogTransitionEnd(dialog);

      if (dialog.open) dialog.close();
    }
  },
);

function waitForDialogTransitionEnd(dialog: HTMLDialogElement) {
  return new Promise<void>((resolve) => {
    const el = dialog;
    const fallback = window.setTimeout(() => resolve(), 300);

    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el) return;
      if (e.propertyName !== 'opacity' && e.propertyName !== 'transform')
        return;

      el.removeEventListener('transitionend', onEnd);
      clearTimeout(fallback);
      resolve();
    };

    el.addEventListener('transitionend', onEnd);
  });
}

function handleOutsideClick(event: MouseEvent) {
  const dialog = dialogRef.value;
  if (!dialog) return;

  if (event.target === lastOpenedByEvent) {
    lastOpenedByEvent = null;
    return;
  }

  const rect = dialog.getBoundingClientRect();
  const clickInside =
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom;

  if (!clickInside && props.isOpen) {
    emit('close');
  }
}

function throttle<F extends (...args: any[]) => any>(func: F, delay: number) {
  let lastCall = 0;

  return (...args: Parameters<F>): ReturnType<F> | undefined => {
    const now = Date.now();
    if (now - lastCall < delay) return undefined;

    lastCall = now;
    return func(...args);
  };
}

function handleScroll() {
  const el = dialogRef.value;
  if (!el || hasTrackedScrollEnd) return;

  const scrollTop = el.scrollTop;
  const clientHeight = el.clientHeight;
  const scrollHeight = el.scrollHeight;
  const threshold = Math.max(10, Math.round(clientHeight * 0.05));

  if (scrollTop + clientHeight >= scrollHeight - threshold) {
    trackModalScroll('view_full_modal', selectedVehicle.value ?? props.vehicle);
    hasTrackedScrollEnd = true;
  }
}

const throttledScrollHandler = throttle(handleScroll, 50);

onMounted(() => {
  window.addEventListener('click', handleOutsideClick);
});

onUnmounted(() => {
  window.removeEventListener('click', handleOutsideClick);

  // Clear scroll timeout if it exists
  if (scrollTimeout) clearTimeout(scrollTimeout);
});

// Selected vehicle (default)
const initialSelectedVehicle = props.vehicle ?? props.vehicles[0] ?? null;
const selectedVehicle = ref(initialSelectedVehicle);

// Rows are based on the selectedVehicle
const computedTables = computed(() =>
  tableTitles
    .map((table) => selectedVehicle.value?.[table as keyof Groups])
    .filter((table) => table && !areAllKeysNull(table)),
);

const tableLabels = computed(() =>
  Object.keys(selectedVehicle.value ?? {})
    .filter(
      (key) =>
        !areAllKeysNull((selectedVehicle.value ?? {})[key as keyof Groups]),
    )
    .filter((key) => tableTitles.includes(key)),
);

function areAllKeysNull(obj: any) {
  for (let key in obj) {
    if (key !== 'label' && obj.hasOwnProperty(key) && obj[key] !== null) {
      return false;
    }
  }
  return true;
}

function getDataPoints(
  table?:
    | VehicleDimensions
    | LoadingSpaceDimensions
    | Weights
    | Body
    | Engine
    | EmissionsConsumption,
  mode?: 'manual' | 'automatic' | null,
) {
  if (!table) return [];

  const skipKeys = new Set(['label', 'engineName']);

  const record = table as Record<string, unknown>;

  const manualBlock = record['manual'] as Record<string, unknown> | undefined;
  const automaticBlock = record['automatic'] as
    | Record<string, unknown>
    | undefined;

  const getNonNullKeys = (obj?: Record<string, unknown>) =>
    obj ? Object.keys(obj).filter((k) => obj[k] != null) : [];

  const hasManual = getNonNullKeys(manualBlock).length > 0;
  const hasAutomatic = getNonNullKeys(automaticBlock).length > 0;

  const chosenMode: 'manual' | 'automatic' | null =
    mode ?? (hasManual ? 'manual' : hasAutomatic ? 'automatic' : null);

  return Object.entries(record).flatMap(([key, raw]) => {
    if (skipKeys.has(key) || raw == null) return [];

    if (key === 'manual') {
      if (chosenMode !== 'manual') return [];
      return getNonNullKeys(raw as Record<string, unknown>);
    }

    if (key === 'automatic') {
      if (chosenMode !== 'automatic') return [];
      return getNonNullKeys(raw as Record<string, unknown>);
    }

    return [key];
  });
}

function getEngineName(v: any) {
  return v?.engine?.engineName ?? v?.vmdsTypeId ?? `${v.trim ?? ''}`;
}

async function handleCancel() {
  if (!props.isOpen) return;

  emit('close');
}

watch(
  () => props.vehicle,
  (v) => {
    selectedVehicle.value = v ?? props.vehicles[0] ?? null;
  },
  { immediate: true },
);

watch(
  () => props.vehicles,
  (list: string | any[]) => {
    if (!selectedVehicle.value && list?.length) {
      selectedVehicle.value = list[0];
    }
  },
  { immediate: true },
);
</script>

<template>
  <dialog
    ref="dialogRef"
    role="dialog"
    aria-modal="true"
    aria-labelledby="tech-data-modal-title"
    class="owvans-tech-data-dialog"
    :class="{ '-animate-in': animatingIn }"
    @cancel.prevent="handleCancel"
  >
    <TechDataModalHeader
      :key="mode ?? 'none'"
      :translations="translations"
      :vehicle="selectedVehicle"
      :validOptions="validOptions"
      :transmissionMode="showTransmissionFilter ? mode : null"
      :close="() => emit('close')"
    />

    <div class="owvans-tech-data-dialog__table-wrapper" ref="tableWrapperRef">
      <div class="owvans-tech-data-dialog__table-scroll" v-has-scroll>
        <table class="wbx-table owvans-tech-data-dialog__table">
          <colgroup>
            <col class="owvans-tech-data-dialog__col-sticky" />
            <col v-for="(_, i) in vehicles" :key="i" />
          </colgroup>
          <thead>
            <tr>
              <th class="owvans-tech-data-dialog__sticky-cell" scope="col">
                <span class="sr-only">
                  {{
                    props.translations['owvc_tech_data_technical_data'] ||
                    getDefaultLocaleValue('technicalData')
                  }}
                </span>
              </th>
              <th
                v-for="(vehicle, index) in vehicles"
                :key="index"
                scope="col"
                class="owvans-tech-data-dialog__labels wb-text-l-strong"
              >
                <span>{{ getEngineName(vehicle) }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(table, tableIndex) in computedTables"
              :key="tableIndex"
            >
              <tr>
                <th
                  class="owvans-tech-data-dialog__section-title"
                  scope="row"
                  :id="`tech-data-modal-title-${tableIndex}`"
                >
                  <span>
                    {{
                      translations[
                        `owvc_tech_data_${tableLabels[tableIndex]}`
                      ] || getDefaultLocaleValue(tableLabels[tableIndex])
                    }}
                  </span>
                </th>

                <th
                  class="owvans-tech-data-dialog__section-title owvans-tech-data-dialog__section-fill"
                  :colspan="vehicles.length"
                  aria-hidden="true"
                ></th>
              </tr>

              <TableValuesItem
                v-for="dataKey in getDataPoints(table, mode)"
                :key="dataKey"
                :vehicles="vehicles"
                :locale="locale"
                :parentLabel="tableLabels[tableIndex]"
                :label="dataKey"
                :translations="translations"
                :transmissionMode="mode"
              />
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </dialog>
</template>

<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';
.owvans-tech-data-dialog {
  --dialog-max-width: min(86%, 105rem);
  --dialog-max-height: 1000px;

  position: fixed;
  inset: 0;

  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;

  padding: 0;

  background-color: var(--wb-color-surface-main-default);
  border: none;

  opacity: 0;
  transform: translateY(50%);
  transition:
    transform var(--wb-duration-medium-2) var(--wb-move),
    opacity var(--wb-duration-medium-2) var(--wb-fade);

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(2px);
    margin: 0;
    padding: 0;
    cursor: pointer;
    transition: var(--_duration) var(--wb-fade);
    opacity: 0;
  }

  &.-animate-in {
    opacity: 1;
    transform: translateY(0);
    &::backdrop {
      opacity: 1;
    }
  }

  @include breakpoint-from(mq4) {
    max-width: var(--dialog-max-width);
    max-height: var(--dialog-max-height);
  }

  :deep(.owvans-tech-data-dialog__col-sticky) {
    width: min(40vw, 210px);
  }

  &__table-wrapper {
    margin-inline-start: var(--wb-spacing-responsive-medium);

    overflow-y: auto;
  }

  &__table-scroll {
    width: 100%;

    margin-bottom: var(--wb-spacing-static-small);

    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: none;

    position: relative;
    isolation: isolate;
  }

  &__table {
    table-layout: auto;

    width: calc(100% - var(--wb-spacing-responsive-medium));

    margin-right: var(--wb-spacing-responsive-medium);
  }

  :deep(.owvans-tech-data-dialog__table th) {
    padding-top: var(--wb-spacing-static-smaller);
    padding-bottom: var(--wb-spacing-static-smaller);

    @media (min-width: 1024px) {
      padding-top: var(--wb-spacing-static-small);
      padding-bottom: var(--wb-spacing-static-small);
    }
  }

  &__sticky-cell {
    position: sticky;
    left: 0;
    max-width: 140px;
    z-index: 1;
    text-align: start;
    background-color: var(--wb-color-surface-main-default);
  }

  .v-has-scroll-x &__sticky-cell::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    transform: translateX(100%);
    bottom: 0;
    width: 4px;
    pointer-events: none;
    background:
      linear-gradient(to right, rgb(0 0 0 / 0.16) 0, transparent 65%),
      linear-gradient(to left, rgb(255 255 255 / 0.28) 0, transparent 55%);
  }

  &__table thead &__sticky-cell {
    background-color: var(--wb-color-background-main);
  }

  :deep(
    .owvans-tech-data-dialog__table
      tbody
      tr:has(+ tr > th.owvans-tech-data-dialog__section-title)
      > th
  ) {
    padding-bottom: var(--wb-spacing-static-medium);
  }

  &__section-title {
    position: sticky;
    left: 0;
    z-index: 4;

    max-width: 140px;

    background: var(--wb-color-shape-weaker-default);
    font-weight: 700;
  }

  .v-has-scroll-x {
    .owvans-tech-data-dialog__table
      .owvans-tech-data-dialog__section-title::after {
      content: none;
    }
  }

  &__section-title > div {
    white-space: nowrap;
  }

  &__section-fill {
    z-index: 0;
    padding: 0;
    border-left: none;
  }

  th {
    text-align: end;
  }

  tr:has(+ tr th.owvans-tech-data-dialog__section-title) {
    border-bottom: none;
  }

  .owvans-tech-data-dialog__section-title,
  &__sticky-cell {
    text-align: start;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
